import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getCommerceId } from '@/lib/ownership';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { downloadInvoicePdf } from '@/lib/invoicePdf';
import type { FinanceInvoice, FinanceInvoiceItem } from '@/lib/finance';
import type { InvoicePayment } from '@/lib/invoicePayments';

interface Props {
  projectId: number;
  project: { id: number; titre: string; budget: string | null; montantpaye: number | null; montantrestant: number | null; clientid: number | null };
  clientName?: string | null;
  onProjectUpdated: () => void;
}

const STATUS_META: Record<string, { label: string; className: string }> = {
  draft: { label: 'Brouillon', className: 'bg-background-200/70 text-foreground-600' },
  sent: { label: 'Envoyée', className: 'bg-secondary-100 text-secondary-700' },
  partially_paid: { label: 'Partiellement payée', className: 'bg-accent-100 text-accent-700' },
  paid: { label: 'Payée', className: 'bg-primary-100 text-primary-700' },
  overdue: { label: 'En retard', className: 'bg-red-100 text-red-600' },
  cancelled: { label: 'Annulée', className: 'bg-background-200/70 text-foreground-500' },
};

function money(amount: number, currency = 'MAD'): string {
  const value = Number.isFinite(amount) ? amount : 0;
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;
}

function parseAmount(raw: unknown): number {
  const n = Number(String(raw ?? '').replace(/[^\d.,-]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Onglet « Finance » d'un projet GestionPro.
 * Relie le projet à Finance : budget, montant facturé, encaissé, restant,
 * factures liées (avec détail des lignes et des règlements) et export PDF.
 */
export default function ProjectFinanceTab({ projectId, project, clientName, onProjectUpdated }: Props) {
  const { user } = useAuth();
  const commerceId = getCommerceId(user);
  const { profile: companyProfile } = useCompanyProfile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [invoices, setInvoices] = useState<FinanceInvoice[]>([]);
  const [candidates, setCandidates] = useState<FinanceInvoice[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [items, setItems] = useState<Record<number, FinanceInvoiceItem[]>>({});
  const [payments, setPayments] = useState<Record<number, InvoicePayment[]>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);

  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState<number | null>(null);

  const currency = invoices[0]?.currency || 'MAD';

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('finance_invoices')
        .select('*')
        .eq('project_id', projectId)
        .order('issue_date', { ascending: false });
      if (err) throw err;
      setInvoices((data || []) as FinanceInvoice[]);
    } catch {
      setError('Impossible de charger les factures du projet.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadCandidates = useCallback(async (): Promise<void> => {
    try {
      const { data } = await supabase
        .from('finance_invoices')
        .select('*')
        .eq('merchant_id', commerceId)
        .is('project_id', null)
        .order('issue_date', { ascending: false })
        .limit(100);
      setCandidates((data || []) as FinanceInvoice[]);
    } catch {
      setCandidates([]);
    }
  }, [commerceId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setBudgetInput(String(parseAmount(project.budget))); }, [project.budget]);

  // Synchronise montant payé / restant sur le projet à partir des factures.
  const syncProjectAmounts = useCallback(async (list: FinanceInvoice[]): Promise<void> => {
    const paid = list
      .filter((inv) => (inv.doc_type || 'invoice') === 'invoice' && inv.status !== 'cancelled')
      .reduce((s, inv) => s + (Number(inv.amount_paid) || 0), 0);
    const budget = parseAmount(project.budget);
    const remaining = Math.max(0, (budget > 0 ? budget : list.reduce((s, inv) => s + (Number(inv.total) || 0), 0)) - paid);
    try {
      await supabase.from('projet').update({
        montantpaye: Math.round(paid),
        montantrestant: Math.round(remaining),
      }).eq('id', projectId);
      onProjectUpdated();
    } catch {
      /* best-effort */
    }
  }, [project.budget, projectId, onProjectUpdated]);

  const totals = useMemo(() => {
    const active = invoices.filter((inv) => inv.status !== 'cancelled');
    const invoiced = active
      .filter((inv) => (inv.doc_type || 'invoice') === 'invoice')
      .reduce((s, inv) => s + (Number(inv.total) || 0), 0);
    const quoted = active
      .filter((inv) => inv.doc_type === 'quote')
      .reduce((s, inv) => s + (Number(inv.total) || 0), 0);
    const paid = active
      .filter((inv) => (inv.doc_type || 'invoice') === 'invoice')
      .reduce((s, inv) => s + (Number(inv.amount_paid) || 0), 0);
    const explicitBudget = parseAmount(project.budget);
    const budget = explicitBudget > 0 ? explicitBudget : invoiced;
    return {
      budget,
      explicitBudget,
      invoiced,
      quoted,
      paid,
      remaining: Math.max(0, budget - paid),
      progress: budget > 0 ? Math.min(100, Math.round((paid / budget) * 100)) : 0,
    };
  }, [invoices, project.budget]);

  const saveBudget = async (): Promise<void> => {
    setSavingBudget(true);
    setNotice('');
    try {
      const value = parseAmount(budgetInput);
      const remaining = Math.max(0, value - totals.paid);
      const { error: err } = await supabase.from('projet').update({
        budget: String(value),
        montantpaye: Math.round(totals.paid),
        montantrestant: Math.round(remaining),
      }).eq('id', projectId);
      if (err) throw err;
      setBudgetOpen(false);
      setNotice('Budget du projet mis à jour.');
      onProjectUpdated();
      setTimeout(() => setNotice(''), 3000);
    } catch {
      setError("Impossible d'enregistrer le budget.");
    } finally {
      setSavingBudget(false);
    }
  };

  const toggleInvoice = async (invoice: FinanceInvoice): Promise<void> => {
    if (expandedId === invoice.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(invoice.id);
    if (items[invoice.id] && payments[invoice.id]) return;
    setDetailLoading(invoice.id);
    try {
      const [itemsRes, payRes] = await Promise.all([
        supabase.from('finance_invoice_items').select('*').eq('invoice_id', invoice.id).order('id'),
        supabase.from('finance_invoice_payments').select('*').eq('invoice_id', invoice.id).order('paid_at', { ascending: false }),
      ]);
      setItems((prev) => ({ ...prev, [invoice.id]: (itemsRes.data || []) as FinanceInvoiceItem[] }));
      setPayments((prev) => ({ ...prev, [invoice.id]: (payRes.data || []) as InvoicePayment[] }));
    } catch {
      setItems((prev) => ({ ...prev, [invoice.id]: [] }));
      setPayments((prev) => ({ ...prev, [invoice.id]: [] }));
    } finally {
      setDetailLoading(null);
    }
  };

  const linkInvoice = async (invoiceId: number): Promise<void> => {
    setLinking(invoiceId);
    setError('');
    try {
      const { error: err } = await supabase.from('finance_invoices').update({ project_id: projectId }).eq('id', invoiceId);
      if (err) throw err;
      setLinkOpen(false);
      await load();
      const { data } = await supabase.from('finance_invoices').select('*').eq('project_id', projectId);
      await syncProjectAmounts((data || []) as FinanceInvoice[]);
      setNotice('Facture reliée au projet.');
      setTimeout(() => setNotice(''), 3000);
    } catch {
      setError("Impossible de relier cette facture.");
    } finally {
      setLinking(null);
    }
  };

  const unlinkInvoice = async (invoiceId: number): Promise<void> => {
    setError('');
    try {
      const { error: err } = await supabase.from('finance_invoices').update({ project_id: null }).eq('id', invoiceId);
      if (err) throw err;
      const next = invoices.filter((inv) => inv.id !== invoiceId);
      setInvoices(next);
      await syncProjectAmounts(next);
      setNotice('Facture détachée du projet.');
      setTimeout(() => setNotice(''), 3000);
    } catch {
      setError('Impossible de détacher cette facture.');
    }
  };

  const downloadPdf = async (invoice: FinanceInvoice): Promise<void> => {
    try {
      let list = items[invoice.id];
      if (!list) {
        const { data } = await supabase.from('finance_invoice_items').select('*').eq('invoice_id', invoice.id).order('id');
        list = (data || []) as FinanceInvoiceItem[];
        setItems((prev) => ({ ...prev, [invoice.id]: list as FinanceInvoiceItem[] }));
      }
      if (!companyProfile?.company_name) {
        setError('Complétez votre profil entreprise dans Finance → Paramètres avant de générer un PDF.');
        return;
      }
      downloadInvoicePdf(invoice, list, companyProfile);
    } catch {
      setError('Impossible de générer le PDF.');
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}
      {notice && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-secondary-50 text-secondary-800 text-sm">
          <i className="ri-checkbox-circle-line"></i>{notice}
        </div>
      )}

      {/* KPI budget */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Budget</p>
            <button
              onClick={() => setBudgetOpen(true)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-foreground-400 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
              title="Définir le budget du projet"
            >
              <i className="ri-pencil-line text-xs"></i>
            </button>
          </div>
          <p className="text-lg font-bold font-heading text-foreground-950">{money(totals.budget, currency)}</p>
          <p className="text-[11px] text-foreground-400 mt-0.5">
            {totals.explicitBudget > 0 ? 'Budget défini' : 'Basé sur les factures'}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide mb-1">Facturé</p>
          <p className="text-lg font-bold font-heading text-foreground-950">{money(totals.invoiced, currency)}</p>
          <p className="text-[11px] text-foreground-400 mt-0.5">{invoices.filter((i) => (i.doc_type || 'invoice') === 'invoice').length} facture(s)</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide mb-1">Encaissé</p>
          <p className="text-lg font-bold font-heading text-primary-700">{money(totals.paid, currency)}</p>
          <p className="text-[11px] text-foreground-400 mt-0.5">{totals.progress}% du budget</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide mb-1">Restant à payer</p>
          <p className={`text-lg font-bold font-heading ${totals.remaining > 0 ? 'text-amber-700' : 'text-secondary-700'}`}>
            {money(totals.remaining, currency)}
          </p>
          <p className="text-[11px] text-foreground-400 mt-0.5">{totals.remaining > 0 ? 'Solde client' : 'Projet soldé'}</p>
        </div>
      </div>

      {/* Progression */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-foreground-600">Avancement du paiement</span>
          <span className="text-xs font-semibold text-foreground-900">{totals.progress}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-background-100 overflow-hidden">
          <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${totals.progress}%` }} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-foreground-500">
          <span><strong className="text-foreground-800">{money(totals.paid, currency)}</strong> payé</span>
          <span><strong className="text-foreground-800">{money(totals.remaining, currency)}</strong> restant</span>
          {totals.quoted > 0 && <span><strong className="text-foreground-800">{money(totals.quoted, currency)}</strong> en devis</span>}
        </div>
      </div>

      {/* Factures liées */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-file-text-line text-primary-500"></i>
            Factures du projet
            <span className="text-[11px] font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">{invoices.length}</span>
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setLinkOpen(true); void loadCandidates(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-link-m"></i>Relier une facture
            </button>
            <a
              href="/dashboard/finance/factures/nouvelle"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline"
            >
              <i className="ri-add-line"></i>Nouvelle facture
            </a>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center px-4">
            <i className="ri-receipt-line text-3xl text-foreground-300 mb-2"></i>
            <p className="text-sm text-foreground-600">Aucune facture liée à ce projet</p>
            <p className="text-xs text-foreground-400 mt-1 max-w-sm">
              Reliez une facture existante, ou créez-en une puis rattachez-la ici. Le client peut payer en une ou plusieurs fois.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {invoices.map((invoice) => {
              const status = STATUS_META[invoice.status || 'draft'] || STATUS_META.draft;
              const isQuote = invoice.doc_type === 'quote';
              const total = Number(invoice.total) || 0;
              const paid = Number(invoice.amount_paid) || 0;
              const rest = Math.max(0, total - paid);
              const ratio = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
              const open = expandedId === invoice.id;
              return (
                <div key={invoice.id}>
                  <div className="px-4 py-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <button
                        onClick={() => void toggleInvoice(invoice)}
                        className="flex items-start gap-2.5 text-left flex-1 min-w-0 cursor-pointer"
                      >
                        <i className={`${open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-foreground-400 mt-0.5 flex-shrink-0`}></i>
                        <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${isQuote ? 'ri-file-list-3-line' : 'ri-file-text-line'} text-foreground-600`}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-foreground-900">{invoice.number}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${status.className}`}>{status.label}</span>
                            {isQuote && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-50 text-accent-700">Devis</span>
                            )}
                          </span>
                          <span className="block text-xs text-foreground-500 mt-0.5 truncate">
                            {invoice.customer_name || clientName || 'Client non renseigné'}
                            {invoice.issue_date ? ` · ${new Date(invoice.issue_date).toLocaleDateString('fr-FR')}` : ''}
                          </span>
                        </span>
                      </button>

                      <div className="flex items-center gap-3 sm:flex-shrink-0 pl-11 sm:pl-0">
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">{money(total, invoice.currency || currency)}</p>
                          <p className={`text-[11px] whitespace-nowrap ${rest > 0 ? 'text-amber-700' : 'text-secondary-700'}`}>
                            {rest > 0 ? `${money(paid, invoice.currency || currency)} payé · ${money(rest, invoice.currency || currency)} restant` : 'Soldée'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => void downloadPdf(invoice)}
                            className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
                            title="Télécharger en PDF"
                          >
                            <i className="ri-download-2-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => void unlinkInvoice(invoice.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                            title="Détacher du projet"
                          >
                            <i className="ri-link-unlink-m text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 ml-11">
                      <div className="h-1.5 rounded-full bg-background-100 overflow-hidden max-w-md">
                        <div className={`h-full rounded-full ${ratio >= 100 ? 'bg-secondary-500' : 'bg-accent-500'}`} style={{ width: `${ratio}%` }} />
                      </div>
                    </div>
                  </div>

                  {open && (
                    <div className="px-4 pb-4 ml-0 sm:ml-11">
                      {detailLoading === invoice.id ? (
                        <div className="py-4 text-center"><i className="ri-loader-4-line animate-spin text-primary-500"></i></div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          {/* Lignes */}
                          <div className="border border-background-200/70 rounded-lg overflow-hidden">
                            <div className="px-3 py-2 bg-background-100/60 text-[11px] font-semibold text-foreground-600 uppercase tracking-wide">
                              Détail des lignes
                            </div>
                            {(items[invoice.id] || []).length === 0 ? (
                              <p className="px-3 py-3 text-xs text-foreground-400">Aucune ligne enregistrée.</p>
                            ) : (
                              <div className="divide-y divide-background-200/50">
                                {(items[invoice.id] || []).map((it) => {
                                  const lineTotal = Math.max(0, (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0));
                                  return (
                                    <div key={it.id} className="px-3 py-2 flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="text-xs font-medium text-foreground-800 truncate">{it.description || '—'}</p>
                                        <p className="text-[11px] text-foreground-400">
                                          {it.quantity} × {money(Number(it.unit_price) || 0, invoice.currency || currency)}
                                          {(Number(it.discount) || 0) > 0 ? ` − ${money(Number(it.discount), invoice.currency || currency)}` : ''}
                                        </p>
                                      </div>
                                      <span className="text-xs font-semibold text-foreground-900 whitespace-nowrap">{money(lineTotal, invoice.currency || currency)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="px-3 py-2 border-t border-background-200/70 bg-background-100/30 text-xs">
                              <div className="flex items-center justify-between text-foreground-600">
                                <span>Total HT</span>
                                <span>{money(Number(invoice.subtotal) || 0, invoice.currency || currency)}</span>
                              </div>
                              {Number(invoice.tax_amount) > 0 && (
                                <div className="flex items-center justify-between text-foreground-600 mt-0.5">
                                  <span>TVA {invoice.tax_rate}%</span>
                                  <span>{money(Number(invoice.tax_amount), invoice.currency || currency)}</span>
                                </div>
                              )}
                              <div className="flex items-center justify-between font-bold text-foreground-950 mt-1 pt-1 border-t border-background-200/60">
                                <span>Total TTC</span>
                                <span>{money(total, invoice.currency || currency)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Règlements */}
                          <div className="border border-background-200/70 rounded-lg overflow-hidden">
                            <div className="px-3 py-2 bg-background-100/60 text-[11px] font-semibold text-foreground-600 uppercase tracking-wide">
                              Règlements enregistrés
                            </div>
                            {(payments[invoice.id] || []).length === 0 ? (
                              <p className="px-3 py-3 text-xs text-foreground-400">Aucun règlement enregistré pour cette facture.</p>
                            ) : (
                              <div className="divide-y divide-background-200/50">
                                {(payments[invoice.id] || []).map((p) => (
                                  <div key={p.id} className="px-3 py-2 flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-xs font-medium text-foreground-800">
                                        {p.paid_at ? new Date(p.paid_at).toLocaleDateString('fr-FR') : '—'}
                                        {p.method ? ` · ${p.method}` : ''}
                                      </p>
                                      {p.note && <p className="text-[11px] text-foreground-400 truncate">{p.note}</p>}
                                    </div>
                                    <span className="text-xs font-semibold text-secondary-700 whitespace-nowrap">
                                      +{money(Number(p.amount) || 0, p.currency || invoice.currency || currency)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="px-3 py-2 border-t border-background-200/70 bg-background-100/30 space-y-1 text-xs">
                              <div className="flex items-center justify-between text-foreground-600">
                                <span>Encaissé</span>
                                <span className="font-semibold text-primary-700">{money(paid, invoice.currency || currency)}</span>
                              </div>
                              <div className="flex items-center justify-between text-foreground-600">
                                <span>Restant dû</span>
                                <span className={`font-semibold ${rest > 0 ? 'text-amber-700' : 'text-secondary-700'}`}>{money(rest, invoice.currency || currency)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modale budget */}
      {budgetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBudgetOpen(false)}>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground-900 mb-1">Budget du projet</h3>
            <p className="text-xs text-foreground-500 mb-4">Montant total convenu avec le client pour ce projet.</p>
            <div className="relative">
              <i className="ri-money-dollar-circle-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="number"
                min={0}
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm font-semibold text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <p className="text-[11px] text-foreground-400 mt-2">
              Laisser vide ou 0 pour calculer le budget à partir des factures liées.
            </p>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => void saveBudget()}
                disabled={savingBudget}
                className="flex-1 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {savingBudget ? <i className="ri-loader-4-line animate-spin"></i> : 'Enregistrer'}
              </button>
              <button onClick={() => setBudgetOpen(false)} className="px-4 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale liaison facture */}
      {linkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setLinkOpen(false)}>
          <div className="bg-background-50 rounded-xl border border-background-200/70 w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-900">Relier une facture au projet</h3>
              <button onClick={() => setLinkOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {candidates.length === 0 ? (
                <div className="py-10 text-center px-4">
                  <i className="ri-file-forbid-line text-3xl text-foreground-300 mb-2"></i>
                  <p className="text-sm text-foreground-600">Aucune facture disponible</p>
                  <p className="text-xs text-foreground-400 mt-1">Toutes vos factures sont déjà reliées à un projet, ou vous n&apos;en avez pas encore créé.</p>
                </div>
              ) : (
                candidates.map((inv) => (
                  <button
                    key={inv.id}
                    onClick={() => void linkInvoice(inv.id)}
                    disabled={linking !== null}
                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${inv.doc_type === 'quote' ? 'ri-file-list-3-line' : 'ri-file-text-line'} text-foreground-600`}></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground-900 truncate">{inv.number} · {inv.customer_name || 'Client'}</span>
                      <span className="block text-[11px] text-foreground-500">
                        {inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('fr-FR') : ''}
                        {inv.doc_type === 'quote' ? ' · Devis' : ''}
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                      {money(Number(inv.total) || 0, inv.currency || currency)}
                    </span>
                    {linking === inv.id && <i className="ri-loader-4-line animate-spin text-primary-500"></i>}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}